/**
 * PATH: packages/email-hub/src/feature/config/navigation.ts
 * FILE_ID: EMAILHUB-FEATURE-CONFIG-NAVIGATION-001
 * PURPOSE: Canonical body of this email config module. Lifted
 *          from apps/<tenant>/src/features/email/config/navigation.ts
 *          (see docs/architecture/EMAIL_KANBAN_CUTOVER_ARC.md).
 *          Tenants import this via thin re-export shims; edit
 *          THIS file, not the tenant copies.
 * WHY:     Removes operator-visible drift between tenants —
 *          previously every email feature change had to be
 *          mirrored manually in two tenant trees, and concurrent
 *          agents kept letting one side go stale. One canonical
 *          body, both tenants consume from this package.
 * RELATED:
 *   - apps/<tenant>/src/features/email/config/navigation.ts: thin re-export
 *     shim that points back here.
 */

import {
  Archive,
  FileText,
  Inbox,
  Send,
  Star,
  Trash2,
  Users,
  type LucideIcon,
} from 'lucide-react'

export interface EmailNavItem {
  id: string
  label: string
  icon: LucideIcon
  href: string
}

// Order is intentional and combines two principles:
//   1. Mirror Gmail's left rail (Inbox / Starred / Drafts / Sent / Archived
//      / Trash) so the operator's muscle memory works.
//   2. Drafts sits between Starred and Sent because conceptually it's
//      "messages on their way out that haven't gone yet" — adjacent to
//      Sent, after the inbound stack.
// Contacts sits below Trash as a soft separator between "things that
// contain emails" and "things that describe the people in those emails".
// Auto-built from email history (see ContactsPage docstring).
// Drafts entry added 2026-05-13 per Mark Newman: "can't see drafts
// figure out where to put drafts.."
export const EMAIL_NAV_ITEMS: EmailNavItem[] = [
  { id: 'inbox', label: 'Inbox', icon: Inbox, href: '/email/inbox' },
  { id: 'starred', label: 'Starred', icon: Star, href: '/email/starred' },
  { id: 'drafts', label: 'Drafts', icon: FileText, href: '/email/drafts' },
  { id: 'sent', label: 'Sent', icon: Send, href: '/email/sent' },
  { id: 'archived', label: 'Archived', icon: Archive, href: '/email/archived' },
  { id: 'trash', label: 'Binned', icon: Trash2, href: '/email/trash' },
  { id: 'contacts', label: 'Contacts', icon: Users, href: '/email/contacts' },
]
